import Margin from "Components/Stats/Margin";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import NivoPie from "Components/Stats/Charts/NivoPie";
import NivoBar from "Components/Stats/Charts/NivoBar";
import PropTypes from "prop-types";
import { fetchGet, useFetchGetConditional } from "Services/api";
import { connect } from "react-redux";
import {
  getOffersByCategoryByMonthData,
  getOffersByCategoryData,
} from "Components/Stats/Data/Offres/Categories";

/**
 * Composant 'OffersByDiffuser'.
 *
 * Ce composant affiche deux graphiques qui représentent les offres par catégorie par diffuseur.
 * L'utilisateur peut sélectionner un diffuseur spécifique à partir d'une liste déroulante.
 * - Le premier graphique est un graphique en camembert qui représente le nombre d'offres par catégorie.
 * - Le deuxième graphique est un graphique en barres qui représente le nombre d'offres par catégorie par mois.
 *
 * - {@link getOffersByCategoryByMonthData} : Fonction qui formate les données pour le graphique des offres par catégorie.
 * - {@link getOffersByCategoryData} : Fonction qui formate les données pour le graphique des offres par catégorie par mois.
 *
 * - {@link NivoPie} : Composant générique de graphique en camembert implémenté avec la librairie Nivo.
 * - {@link NivoBar} : Composant générique de graphique en barres implémenté avec la librairie Nivo.
 *
 * Les données 'API' actuellement utilisées dans le composant sont aléatoires / fictives.
 *
 * @param {Object} props - Les propriétés du composant.
 * @param {Object} props.auth - Les informations d'authentification de l'utilisateur (depuis Redux).
 * @param {String} props.auth.token - Le token de l'utilisateur connecté.
 * @param {Object} props.secondaryTables - Les données des tables secondaires (depuis Redux)
 * @param {Array<{label: String, id: Number}>|undefined} props.secondaryTables.offerCategoriesData - Liste des catégories d'offres.
 * @param {Array<{name: String, id: Number}>|undefined} props.secondaryTables.diffusersData - Liste des diffuseurs
 * @param {Object} props.states - Les données concernant les états (depuis Redux).
 * @param {Array<{statusLabel: String, id: Number}>} props.states.statesData - Liste des états
 * @param {string} props.universityName - Le nom de l'université pour laquelle afficher les statistiques.
 * @returns {JSX.Element} Les graphiques des offres par catégorie par diffuseur.
 *
 * @example
 * // Utilisation du composant 'OffersByDiffuser' dans un autre composant.
 * import OffersByDiffuser from './OffersByDiffuser';
 * // ...
 * <OffersByDiffuser />
 */
const OffersByDiffuser = (props) => {
  const op = useRef();

  const [selectedDiffuser, setSelectedDiffuser] = useState(undefined);

  const [offersByDiffuserByCategory, setOffersByDiffuserByCategory] = useState(
    []
  );
  const [
    offersByDiffuserByCategoryMonthly,
    setOffersByDiffuserByCategoriesMonthlyData,
  ] = useState([]);

  const diffusersList = useFetchGetConditional(
    "/diffusers",
    props.auth.token,
    props.secondaryTables.diffusersData
  );

  //? Date il y a 7 mois
  const dateStart = new Date(
    new Date().getFullYear(),
    new Date().getMonth() - 7,
    1
  );
  //? Date il y a 1 mois
  const dateEnd = new Date(
    new Date().getFullYear(),
    new Date().getMonth() - 1,
    1
  );

  /**
   * Récupère les données pour le graphique des offres par diffuseur
   * par catégorie
   *
   * @param {String} diffuserName - Nom du diffuseur
   */
  const makeCategoryRequest = async (diffuserName) => {
    const requestData = await fetchGet(
      "/offers/stats/count/category",
      props.auth.token,
      {
        diffuser: diffusersList.data?.find(
          (diffuser) => diffuser?.name === diffuserName
        ).id,
        // Filtre état: publié
        "state[]": props.states.statesData.find(
          (state) => state.statusLabel === "Publié"
        ).id,
      }
    );

    setOffersByDiffuserByCategory(
      getOffersByCategoryData(
        requestData,
        props.secondaryTables.offerCategoriesData || []
      )
    );
  };

  /**
   * Récupère les données pour le graphique des offres par diffuseur
   * par catégorie par mois
   *
   * @param {String} diffuserName - Nom du diffuseur
   */
  const makeCategoryMonthlyRequest = async (diffuserName) => {
    const requestData = await fetchGet(
      "/offers/stats/count/category",
      props.auth.token,
      {
        diffuser: diffusersList.data?.find(
          (diffuser) => diffuser?.name === diffuserName
        ).id,
        "postedAt[after]": dateStart.toISOString().slice(0, 10),
        "postedAt[before]": dateEnd.toISOString().slice(0, 10),
        // Filtre état: publié
        "state[]": [
          props.states.statesData.find(
            (state) => state.statusLabel === "Publié"
          ).id,
        ],
      }
    );

    setOffersByDiffuserByCategoriesMonthlyData(
      getOffersByCategoryByMonthData(
        requestData || [],
        props.secondaryTables.offerCategoriesData,
        dateStart,
        dateEnd
      )
    );
  };

  useEffect(async () => {
    if (selectedDiffuser === undefined) return;
    await makeCategoryRequest(selectedDiffuser);
    await makeCategoryMonthlyRequest(selectedDiffuser);
  }, [selectedDiffuser]);

  useEffect(() => {
    if (diffusersList.loaded) {
      setSelectedDiffuser(diffusersList.data?.at(0)?.name || undefined);
    }
  }, [diffusersList.loaded]);

  const handleClick = (e) => {
    setSelectedDiffuser(e.target.outerText);
    op.current.hide();
  };

  return (
    <React.Fragment>
      <Button
        type="button"
        style={{
          //? Centrer le bouton
          display: "block",
          margin: "auto",
          width: "fit-content",
        }}
        label={selectedDiffuser}
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel ref={op} className="stats-panel">
        {diffusersList.loaded &&
          diffusersList.data?.map((diffuser) => (
            <Button
              key={diffuser.id}
              id={diffuser.id}
              label={diffuser?.name}
              onClick={handleClick}
            />
          ))}
      </OverlayPanel>
      <Margin position="bottom" size="xs" />
      <NivoPie
        data={offersByDiffuserByCategory}
        title={
          "Nombre d'offres par catégorie en ligne à ce jour du diffuseur " +
          selectedDiffuser +
          " dans l'université " +
          props.universityName
        }
        labelName="Catégorie"
        legendWidth={115}
        displayPercentage
        colors="pastel1"
      />
      <Margin position="bottom" size="" />
      <NivoBar
        data={offersByDiffuserByCategoryMonthly}
        keysNames={props.secondaryTables.offerCategoriesData.map(
          (category) => category.label
        )}
        legendYAxis="Offres par catégorie"
        indexName="date"
        displayPercentage
        title={
          "Nombre d'offres en lignes mensuelles par catégorie du diffuseur " +
          selectedDiffuser +
          " dans l'université " +
          props.universityName
        }
        colors="pastel1"
      />
      <Margin position="bottom" size="" />
    </React.Fragment>
  );
};

OffersByDiffuser.propTypes = {
  auth: PropTypes.object.isRequired,
  states: PropTypes.object.isRequired,
  secondaryTables: PropTypes.object.isRequired,
  universityName: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  states: state.states,
  auth: state.auth,
  secondaryTables: state.secondaryTables,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(OffersByDiffuser);
